import { sanitizeHtml } from "../utils/sanitize";
import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { copyToClipboard } from '../utils/clipboard';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Plus, Save, Trash2, Edit, Settings as SettingsIcon, Ticket, Layout, Palette, MessageSquare, Mail, Copy, Eye, X as XIcon, Zap, Package, UserCog, Reply, ToggleLeft, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import RichTextEditor from '../components/RichTextEditor';
import NotificationSettings from '../components/NotificationSettings';
import { Switch } from '../components/ui/switch';

// ============================================================================
// AUTO-REPLY SETTINGS COMPONENT
// ============================================================================
const AutoReplySettings = () => {
  const [arSettings, setArSettings] = useState(null);
  const [arLoading, setArLoading] = useState(true);
  const [arSaving, setArSaving] = useState(false);
  const [mailboxes, setMailboxes] = useState([]);
  const [previewHtml, setPreviewHtml] = useState(null);
  const [editorMode, setEditorMode] = useState('visual'); // 'visual' or 'html'

  useEffect(() => {
    loadAutoReplyData();
  }, []);

  const loadAutoReplyData = async () => {
    try {
      const [settingsRes, mailboxRes] = await Promise.all([
        api.get('/auto-reply/settings'),
        api.get('/auto-reply/mailboxes').catch(() => ({ data: [] })),
      ]);
      setArSettings(settingsRes.data);
      setMailboxes(mailboxRes.data);
    } catch (err) {
      console.error('Failed to load auto-reply settings:', err);
    } finally {
      setArLoading(false);
    }
  };

  const saveSettings = async (updates) => {
    setArSaving(true);
    try {
      const res = await api.put('/auto-reply/settings', updates);
      setArSettings(res.data);
      toast.success('Auto-reply settings saved');
    } catch (err) {
      console.error('Failed to save:', err);
      toast.error('Failed to save settings');
    } finally {
      setArSaving(false);
    }
  };

  const handlePreview = async () => {
    try {
      const res = await api.post('/auto-reply/preview', {
        subject_template: arSettings.subject_template,
        body_template: arSettings.body_template,
      });
      setPreviewHtml(res.data);
    } catch (err) {
      toast.error('Template preview failed: ' + (err.response?.data?.detail || err.message));
    }
  };

  if (arLoading) return <Card><CardContent className="p-8 text-center text-gray-500">Loading auto-reply settings...</CardContent></Card>;
  if (!arSettings) return null;

  const variables = [
    { name: '{{ticket_number}}', desc: 'Ticket number' },
    { name: '{{ticket_title}}', desc: 'Ticket subject' },
    { name: '{{customer_name}}', desc: 'Customer name' },
    { name: '{{customer_email}}', desc: 'Customer email' },
    { name: '{{ticket_priority}}', desc: 'Priority level' },
    { name: '{{ticket_status}}', desc: 'Status' },
    { name: '{{latest_comment}}', desc: 'The comment/message that triggered this email' },
  ];

  return (
    <div className="space-y-4">
      {/* Enable/Disable Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2" data-testid="auto-reply-title">
                <Reply className="h-5 w-5" /> Ticket Auto-Reply
              </CardTitle>
              <CardDescription>
                Automatically send a confirmation email to the person who submitted a ticket.
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Label htmlFor="ar-enabled" className="text-sm font-medium">
                {arSettings.enabled ? 'Enabled' : 'Disabled'}
              </Label>
              <Switch
                id="ar-enabled"
                data-testid="auto-reply-enabled-toggle"
                checked={arSettings.enabled}
                onCheckedChange={(val) => {
                  setArSettings(prev => ({ ...prev, enabled: val }));
                  saveSettings({ enabled: val });
                }}
              />
            </div>
          </div>
        </CardHeader>

        {arSettings.enabled && (
          <CardContent className="space-y-6">
            {/* Trigger sources */}
            <div>
              <Label className="text-sm font-semibold mb-2 block">Send auto-reply for tickets created via:</Label>
              <div className="flex flex-wrap gap-4">
                {[
                  { key: 'reply_to_email_tickets', label: 'Email Import' },
                  { key: 'reply_to_manual_tickets', label: 'Manual / Staff' },
                  { key: 'reply_to_portal_tickets', label: 'Client Portal' },
                ].map(({ key, label }) => (
                  <label key={key} className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      data-testid={`auto-reply-${key}`}
                      checked={arSettings[key] !== false}
                      onChange={(e) => {
                        setArSettings(prev => ({ ...prev, [key]: e.target.checked }));
                      }}
                      className="rounded border-gray-300"
                    />
                    {label}
                  </label>
                ))}
              </div>
            </div>

            {/* Mailbox selector */}
            <div>
              <Label className="text-sm font-semibold">Send from mailbox</Label>
              <Select
                value={arSettings.mailbox_id || '__auto__'}
                onValueChange={(val) => setArSettings(prev => ({ ...prev, mailbox_id: val === '__auto__' ? '' : val }))}
              >
                <SelectTrigger className="mt-1" data-testid="auto-reply-mailbox-select">
                  <SelectValue placeholder="Select mailbox" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__auto__">Auto (first active mailbox)</SelectItem>
                  {mailboxes.map(m => (
                    <SelectItem key={m.id} value={m.id}>{m.user_email}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {mailboxes.length === 0 && (
                <p className="text-xs text-amber-600 mt-1">No Office 365 mailboxes connected. Connect one in Email Settings.</p>
              )}
            </div>

            {/* Subject template */}
            <div>
              <Label className="text-sm font-semibold">Subject Template</Label>
              <Input
                data-testid="auto-reply-subject-input"
                className="mt-1"
                value={arSettings.subject_template || ''}
                onChange={(e) => setArSettings(prev => ({ ...prev, subject_template: e.target.value }))}
                placeholder="Re: {{ticket_title}} [Ticket #{{ticket_number}}]"
              />
            </div>

            {/* Body template with visual/HTML toggle */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label className="text-sm font-semibold">Body Template</Label>
                <div className="flex items-center border rounded-md overflow-hidden text-xs">
                  <button
                    type="button"
                    data-testid="auto-reply-mode-visual"
                    className={`px-3 py-1 transition-colors ${editorMode === 'visual' ? 'bg-gray-900 text-white' : 'bg-[#0b2130] text-[#b1c3ce] hover:bg-white/10'}`}
                    onClick={() => setEditorMode('visual')}
                  >
                    Visual
                  </button>
                  <button
                    type="button"
                    data-testid="auto-reply-mode-html"
                    className={`px-3 py-1 transition-colors ${editorMode === 'html' ? 'bg-gray-900 text-white' : 'bg-[#0b2130] text-[#b1c3ce] hover:bg-white/10'}`}
                    onClick={() => setEditorMode('html')}
                  >
                    HTML
                  </button>
                </div>
              </div>
              {editorMode === 'visual' ? (
                <RichTextEditor
                  key={`ar-editor-${arSettings.body_template?.length > 100 ? 'loaded' : 'empty'}`}
                  content={arSettings.body_template || ''}
                  onChange={(html) => setArSettings(prev => ({ ...prev, body_template: html }))}
                  placeholder="Compose your auto-reply message..."
                  minHeight="260px"
                />
              ) : (
                <Textarea
                  data-testid="auto-reply-body-textarea"
                  className="mt-1 min-h-[300px] font-mono text-xs"
                  value={arSettings.body_template || ''}
                  onChange={(e) => setArSettings(prev => ({ ...prev, body_template: e.target.value }))}
                  placeholder="Enter your auto-reply HTML template..."
                />
              )}
              <p className="text-xs text-gray-500 mt-1">
                {editorMode === 'visual'
                  ? 'Type template variables like {{ticket_number}} directly in the editor. They will be replaced with real data when sent.'
                  : 'Edit the raw HTML. Use Jinja2 variables like {{ticket_number}} for dynamic content.'}
              </p>
            </div>

            {/* Template variables reference */}
            <div>
              <Label className="text-sm font-semibold mb-2 block">Available Variables</Label>
              <div className="flex flex-wrap gap-2">
                {variables.map(v => (
                  <Badge
                    key={v.name}
                    variant="secondary"
                    className="cursor-pointer text-xs font-mono hover:bg-blue-100"
                    title={v.desc}
                    onClick={() => {
                      navigator.clipboard.writeText(v.name);
                      toast.success(`Copied ${v.name}`);
                    }}
                  >
                    {v.name}
                  </Badge>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-1">Click to copy. Use these in your subject and body templates.</p>
            </div>

            {/* Action buttons */}
            <div className="flex gap-2 pt-2 border-t">
              <Button
                data-testid="auto-reply-save-btn"
                onClick={() => saveSettings({
                  subject_template: arSettings.subject_template,
                  body_template: arSettings.body_template,
                  mailbox_id: arSettings.mailbox_id,
                  reply_to_email_tickets: arSettings.reply_to_email_tickets,
                  reply_to_manual_tickets: arSettings.reply_to_manual_tickets,
                  reply_to_portal_tickets: arSettings.reply_to_portal_tickets,
                })}
                disabled={arSaving}
              >
                {arSaving ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Saving...</> : <><Save className="h-4 w-4 mr-1" /> Save Template</>}
              </Button>
              <Button variant="outline" onClick={handlePreview} data-testid="auto-reply-preview-btn">
                <Eye className="h-4 w-4 mr-1" /> Preview
              </Button>
            </div>

            {/* Preview panel */}
            {previewHtml && (
              <div className="border rounded-lg overflow-hidden">
                <div className="bg-gray-50 px-4 py-2 border-b flex items-center justify-between">
                  <span className="text-sm font-medium">Preview (Sample Data)</span>
                  <Button variant="ghost" size="sm" className="h-6" onClick={() => setPreviewHtml(null)}>
                    <XIcon className="h-3 w-3" />
                  </Button>
                </div>
                <div className="p-3 bg-gray-50">
                  <div className="text-sm"><strong>Subject:</strong> {previewHtml.subject}</div>
                </div>
                <div className="p-4 bg-white" dangerouslySetInnerHTML={{ __html: sanitizeHtml(previewHtml.body_html) }} />
              </div>
            )}
          </CardContent>
        )}
      </Card>
    </div>
  );
};

export default function TicketSettings() {
  const [settings, setSettings] = useState(null);
  const [statuses, setStatuses] = useState([]);
  const [boards, setBoards] = useState([]);
  const [templateResponses, setTemplateResponses] = useState([]);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const [settingsForm, setSettingsForm] = useState({
    ticket_prefix: 'TKT',
    ticket_next_number: 1,
    randomize_ticket_numbers: false,
    default_priority: 'medium',
    default_category: 'other'
  });
  
  const [statusForm, setStatusForm] = useState({
    name: '',
    display_name: '',
    color: 'gray',
    is_active: true,
    order: 0
  });
  
  const [boardForm, setBoardForm] = useState({
    name: '',
    description: '',
    board_type: 'kanban',
    is_active: true,
    is_default: false,
    allowed_statuses: []
  });

  const [templateForm, setTemplateForm] = useState({
    name: '',
    category: '',
    trigger_keywords: [],
    template_text: '',
    variables: [],
    is_active: true
  });

  const [emailTemplateForm, setEmailTemplateForm] = useState({
    name: '',
    template_type: 'custom',
    subject: '',
    body_html: '',
    merge_fields: [],
    is_active: true,
    description: ''
  });
  
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [boardDialogOpen, setBoardDialogOpen] = useState(false);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [emailTemplateDialogOpen, setEmailTemplateDialogOpen] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewContent, setPreviewContent] = useState({ subject: '', body: '' });
  
  const [editingStatus, setEditingStatus] = useState(null);
  const [editingBoard, setEditingBoard] = useState(null);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [editingEmailTemplate, setEditingEmailTemplate] = useState(null);

  const [routingRules, setRoutingRules] = useState([]);
  const [routingDialogOpen, setRoutingDialogOpen] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [routingForm, setRoutingForm] = useState({
    name: '', asset_type: '', asset_status: '', target_board_id: '', target_board_name: '', suggested_priority: 'medium', is_active: true, priority_order: 99,
    auto_assign: false, assign_strategy: 'round_robin', assign_technician_id: '', assign_technician_name: ''
  });

  const [technicians, setTechnicians] = useState([]);
  
  const [keywordInput, setKeywordInput] = useState('');
  const [variableInput, setVariableInput] = useState('');

  // Available merge fields for email templates
  const availableMergeFields = [
    'customer_name', 'customer_email', 'ticket_number', 'ticket_title',
    'ticket_status', 'ticket_priority', 'assigned_technician',
    'created_date', 'updated_date', 'ticket_link', 'latest_comment'
  ];

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [settingsRes, statusesRes, boardsRes, templatesRes, emailTemplatesRes, routingRes, usersRes] = await Promise.all([
        api.get('/ticket-settings'),
        api.get('/ticket-statuses'),
        api.get('/ticket-boards'),
        api.get('/template-responses').catch(() => ({ data: [] })),
        api.get('/email-templates').catch(() => ({ data: [] })),
        api.get('/asset-routing-rules').catch(() => ({ data: [] })),
        api.get('/users').catch(() => ({ data: [] }))
      ]);
      
      setSettings(settingsRes.data);
      setSettingsForm({
        ticket_prefix: settingsRes.data.ticket_prefix || 'TKT',
        ticket_next_number: settingsRes.data.ticket_next_number || 1,
        randomize_ticket_numbers: settingsRes.data.randomize_ticket_numbers || false,
        default_priority: settingsRes.data.default_priority || 'medium',
        default_category: settingsRes.data.default_category || 'other'
      });
      
      setStatuses(statusesRes.data);
      setBoards(boardsRes.data);
      setTemplateResponses(templatesRes.data);
      setEmailTemplates(emailTemplatesRes.data);
      setRoutingRules(routingRes.data);
      setTechnicians((usersRes.data || []).filter(u => u.role === 'admin' || u.role === 'technician'));
    } catch (error) {
      console.error('Ticket settings load error:', error);
      if (error.response?.status === 401) {
        toast.error('Please log in to view ticket settings');
      } else {
        toast.error('Failed to load ticket settings. Please check your connection.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      await api.put('/ticket-settings', settingsForm);
      toast.success('Ticket settings updated successfully');
      loadData();
    } catch (error) {
      toast.error('Failed to update settings');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveStatus = async () => {
    try {
      if (editingStatus) {
        await api.put(`/ticket-statuses/${editingStatus.id}`, statusForm);
        toast.success('Status updated successfully');
      } else {
        await api.post('/ticket-statuses', statusForm);
        toast.success('Status created successfully');
      }
      setStatusDialogOpen(false);
      resetStatusForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save status');
    }
  };

  const handleDeleteStatus = async (statusId) => {
    if (!window.confirm('Are you sure you want to delete this status?')) return;
    
    try {
      await api.delete(`/ticket-statuses/${statusId}`);
      toast.success('Status deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete status');
    }
  };

  const handleEditStatus = (status) => {
    setEditingStatus(status);
    setStatusForm({
      name: status.name,
      display_name: status.display_name,
      color: status.color,
      is_active: status.is_active,
      order: status.order
    });
    setStatusDialogOpen(true);
  };

  const resetStatusForm = () => {
    setEditingStatus(null);
    setStatusForm({
      name: '',
      display_name: '',
      color: 'gray',
      is_active: true,
      order: 0
    });
  };

  const handleSaveBoard = async () => {
    try {
      if (editingBoard) {
        await api.put(`/ticket-boards/${editingBoard.id}`, boardForm);
        toast.success('Board updated successfully');
      } else {
        await api.post('/ticket-boards', boardForm);
        toast.success('Board created successfully');
      }
      setBoardDialogOpen(false);
      resetBoardForm();
      loadData();
    } catch (error) {
      toast.error('Failed to save board');
    }
  };

  const handleDeleteBoard = async (boardId) => {
    if (!window.confirm('Are you sure you want to delete this board?')) return;
    
    try {
      await api.delete(`/ticket-boards/${boardId}`);
      toast.success('Board deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete board');
    }
  };

  const handleEditBoard = (board) => {
    setEditingBoard(board);
    setBoardForm({
      name: board.name,
      description: board.description || '',
      board_type: board.board_type,
      is_active: board.is_active,
      is_default: board.is_default || false,
      allowed_statuses: board.allowed_statuses || []
    });
    setBoardDialogOpen(true);
  };

  const resetBoardForm = () => {
    setEditingBoard(null);
    setBoardForm({
      name: '',
      description: '',
      board_type: 'kanban',
      is_active: true,
      is_default: false,
      allowed_statuses: []
    });
  };

  // Template Response Handlers
  const handleSaveTemplate = async () => {
    try {
      if (editingTemplate) {
        await api.put(`/template-responses/${editingTemplate.id}`, templateForm);
        toast.success('Template updated successfully');
      } else {
        await api.post('/template-responses', templateForm);
        toast.success('Template created successfully');
      }
      setTemplateDialogOpen(false);
      resetTemplateForm();
      loadData();
    } catch (error) {
      toast.error('Failed to save template');
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;
    
    try {
      await api.delete(`/template-responses/${templateId}`);
      toast.success('Template deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete template');
    }
  };

  const handleEditTemplate = (template) => {
    setEditingTemplate(template);
    setTemplateForm({
      name: template.name,
      category: template.category || '',
      trigger_keywords: template.trigger_keywords || [],
      template_text: template.template_text,
      variables: template.variables || [],
      is_active: template.is_active
    });
    setTemplateDialogOpen(true);
  };

  const resetTemplateForm = () => {
    setEditingTemplate(null);
    setTemplateForm({
      name: '',
      category: '',
      trigger_keywords: [],
      template_text: '',
      variables: [],
      is_active: true
    });
    setKeywordInput('');
    setVariableInput('');
  };

  const addKeyword = () => {
    if (keywordInput.trim() && !templateForm.trigger_keywords.includes(keywordInput.trim())) {
      setTemplateForm({
        ...templateForm,
        trigger_keywords: [...templateForm.trigger_keywords, keywordInput.trim()]
      });
      setKeywordInput('');
    }
  };

  const removeKeyword = (keyword) => {
    setTemplateForm({
      ...templateForm,
      trigger_keywords: templateForm.trigger_keywords.filter(k => k !== keyword)
    });
  };

  const addVariable = () => {
    if (variableInput.trim() && !templateForm.variables.includes(variableInput.trim())) {
      setTemplateForm({
        ...templateForm,
        variables: [...templateForm.variables, variableInput.trim()]
      });
      setVariableInput('');
    }
  };

  const removeVariable = (variable) => {
    setTemplateForm({
      ...templateForm,
      variables: templateForm.variables.filter(v => v !== variable)
    });
  };

  const insertVariable = (variable) => {
    const placeholder = `{{${variable}}}`;
    setTemplateForm({
      ...templateForm,
      template_text: templateForm.template_text + placeholder
    });
  };

  // Email Template Handlers
  const handleSaveEmailTemplate = async () => {
    try {
      if (editingEmailTemplate) {
        await api.put(`/email-templates/${editingEmailTemplate.id}`, emailTemplateForm);
        toast.success('Email template updated successfully');
      } else {
        await api.post('/email-templates', emailTemplateForm);
        toast.success('Email template created successfully');
      }
      setEmailTemplateDialogOpen(false);
      resetEmailTemplateForm();
      loadData();
    } catch (error) {
      toast.error('Failed to save email template');
    }
  };

  const handleDeleteEmailTemplate = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this email template?')) return;
    
    try {
      await api.delete(`/email-templates/${templateId}`);
      toast.success('Email template deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete email template');
    }
  };

  const handleEditEmailTemplate = (template) => {
    setEditingEmailTemplate(template);
    setEmailTemplateForm({
      name: template.name,
      template_type: template.template_type,
      subject: template.subject,
      body_html: template.body_html,
      merge_fields: template.merge_fields || [],
      is_active: template.is_active,
      description: template.description || ''
    });
    setEmailTemplateDialogOpen(true);
  };

  const resetEmailTemplateForm = () => {
    setEditingEmailTemplate(null);
    setEmailTemplateForm({
      name: '',
      template_type: 'custom',
      subject: '',
      body_html: '',
      merge_fields: [],
      is_active: true,
      description: ''
    });
  };

  const insertMergeField = (field) => {
    const placeholder = `{{${field}}}`;
    setEmailTemplateForm({
      ...emailTemplateForm,
      subject: emailTemplateForm.subject + placeholder
    });
  };

  const toggleMergeField = (field) => {
    const fields = emailTemplateForm.merge_fields;
    if (fields.includes(field)) {
      setEmailTemplateForm({
        ...emailTemplateForm,
        merge_fields: fields.filter(f => f !== field)
      });
    } else {
      setEmailTemplateForm({
        ...emailTemplateForm,
        merge_fields: [...fields, field]
      });
    }
  };

  const handlePreviewEmail = () => {
    // Replace merge fields with sample data for preview
    const sampleData = {
      customer_name: 'John Doe',
      customer_email: 'john@example.com',
      ticket_number: '0126-ABC123',
      ticket_title: 'Sample Ticket Title',
      ticket_status: 'Open',
      ticket_priority: 'High',
      assigned_technician: 'Tech Support',
      created_date: new Date().toLocaleDateString(),
      updated_date: new Date().toLocaleDateString(),
      ticket_link: 'https://yourapp.com/tickets/123'
    };

    let previewSubject = emailTemplateForm.subject;
    let previewBody = emailTemplateForm.body_html;

    Object.keys(sampleData).forEach(key => {
      const regex = new RegExp(`{{${key}}}`, 'g');
      previewSubject = previewSubject.replace(regex, sampleData[key]);
      previewBody = previewBody.replace(regex, sampleData[key]);
    });

    setPreviewContent({ subject: previewSubject, body: previewBody });
    setPreviewDialogOpen(true);
  };

  const colorOptions = [
    { value: 'gray', label: 'Gray', class: 'bg-gray-100 text-gray-800' },
    { value: 'blue', label: 'Blue', class: 'bg-blue-100 text-blue-800' },
    { value: 'green', label: 'Green', class: 'bg-green-100 text-green-800' },
    { value: 'yellow', label: 'Yellow', class: 'bg-yellow-100 text-yellow-800' },
    { value: 'orange', label: 'Orange', class: 'bg-orange-100 text-orange-800' },
    { value: 'red', label: 'Red', class: 'bg-red-100 text-red-800' },
    { value: 'purple', label: 'Purple', class: 'bg-purple-100 text-purple-800' }
  ];

  const handleSaveRoutingRule = async () => {
    try {
      const board = boards.find(b => b.id === routingForm.target_board_id);
      const tech = technicians.find(t => t.email === routingForm.assign_technician_id);
      const payload = {
        ...routingForm,
        target_board_name: board?.name || '',
        assign_technician_name: tech ? (tech.display_name || tech.email.split('@')[0]) : ''
      };
      if (editingRule) {
        await api.put(`/asset-routing-rules/${editingRule.id}`, payload);
        toast.success('Rule updated');
      } else {
        payload.priority_order = routingRules.length + 1;
        await api.post('/asset-routing-rules', payload);
        toast.success('Rule created');
      }
      setRoutingDialogOpen(false);
      setEditingRule(null);
      setRoutingForm({ name: '', asset_type: '', asset_status: '', target_board_id: '', target_board_name: '', suggested_priority: 'medium', is_active: true, priority_order: 99, auto_assign: false, assign_strategy: 'round_robin', assign_technician_id: '', assign_technician_name: '' });
      loadData();
    } catch { toast.error('Failed to save rule'); }
  };

  const handleDeleteRoutingRule = async (ruleId) => {
    try {
      await api.delete(`/asset-routing-rules/${ruleId}`);
      toast.success('Rule deleted');
      loadData();
    } catch { toast.error('Failed to delete rule'); }
  };

  const openEditRule = (rule) => {
    setEditingRule(rule);
    setRoutingForm({
      name: rule.name || '', asset_type: rule.asset_type || '', asset_status: rule.asset_status || '',
      target_board_id: rule.target_board_id || '', target_board_name: rule.target_board_name || '',
      suggested_priority: rule.suggested_priority || 'medium', is_active: rule.is_active !== false, priority_order: rule.priority_order || 99,
      auto_assign: rule.auto_assign || false, assign_strategy: rule.assign_strategy || 'round_robin',
      assign_technician_id: rule.assign_technician_id || '', assign_technician_name: rule.assign_technician_name || ''
    });
    setRoutingDialogOpen(true);
  };

  if (loading) {
    return (
      <div className="co-page co-scope co-settings flex flex-col items-center justify-center min-h-[60vh]">
        <img src="/cipherone-logo.png" alt="Loading" className="h-16 w-16 logo-spin mb-4" />
        <div className="text-lg text-gray-600">Loading settings...</div>
      </div>
    );
  }

  return (
    <div className="co-page co-scope co-settings space-y-6" data-testid="ticket-settings-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold">Ticket Settings</h1>
        <p className="text-gray-600 mt-1">Configure ticket numbering, statuses, boards, and templates</p>
      </div>

      <Tabs defaultValue="general" className="space-y-4">
        <TabsList>
          <TabsTrigger value="general">General Settings</TabsTrigger>
          <TabsTrigger value="statuses">Custom Statuses</TabsTrigger>
          <TabsTrigger value="boards">Ticket Boards</TabsTrigger>
          <TabsTrigger value="notifications">Notifications</TabsTrigger>
          <TabsTrigger value="templates" data-testid="templates-tab">Template Responses</TabsTrigger>
          <TabsTrigger value="emails" data-testid="emails-tab">Email Templates</TabsTrigger>
          <TabsTrigger value="autoreply" data-testid="autoreply-tab">Auto-Reply</TabsTrigger>
          <TabsTrigger value="routing" data-testid="routing-tab">Asset Routing</TabsTrigger>
        </TabsList>

        {/* General Settings Tab */}
        <TabsContent value="general">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <SettingsIcon className="h-5 w-5" />
                Ticket Number Configuration
              </CardTitle>
              <CardDescription>
                Configure how ticket numbers are generated and formatted
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <Card className="bg-amber-50 border-amber-200">
                <CardContent className="p-4">
                  <h3 className="font-semibold mb-1 flex items-center gap-2">
                    <Ticket className="h-4 w-4" />
                    Ticket Number Format
                  </h3>
                  <p className="text-sm text-gray-700">
                    Tickets are automatically numbered as <span className="font-mono font-bold">MMDD-XXXXXX</span> where:
                  </p>
                  <ul className="text-sm text-gray-700 mt-2 space-y-1 ml-4">
                    <li>• <strong>MMDD</strong> = Current month and day (e.g., 0124 for January 24th)</li>
                    <li>• <strong>XXXXXX</strong> = 6 random alphanumeric characters for security</li>
                  </ul>
                </CardContent>
              </Card>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Default Priority</Label>
                  <Select
                    value={settingsForm.default_priority}
                    onValueChange={(value) => setSettingsForm({...settingsForm, default_priority: value})}
                  >
                    <SelectTrigger data-testid="default-priority-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="critical">Critical</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Default Category</Label>
                  <Select
                    value={settingsForm.default_category}
                    onValueChange={(value) => setSettingsForm({...settingsForm, default_category: value})}
                  >
                    <SelectTrigger data-testid="default-category-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="hardware">Hardware</SelectItem>
                      <SelectItem value="software">Software</SelectItem>
                      <SelectItem value="network">Network</SelectItem>
                      <SelectItem value="security">Security</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex justify-end">
                <Button onClick={handleSaveSettings} disabled={saving} data-testid="save-settings-button">
                  <Save className="h-4 w-4 mr-2" />
                  {saving ? 'Saving...' : 'Save Settings'}
                </Button>
              </div>

              <Card className="bg-blue-50 border-blue-200">
                <CardContent className="p-4">
                  <p className="text-sm font-semibold text-gray-700 mb-2">Next Ticket Number Preview:</p>
                  <p className="text-2xl font-mono font-bold text-blue-600">
                    {(() => {
                      const now = new Date();
                      const mmdd = `${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}`;
                      const random = Math.random().toString(36).substring(2, 8).toUpperCase();
                      return `${mmdd}-${random}`;
                    })()}
                  </p>
                  <p className="text-xs text-gray-500 mt-2">Format: MMDD-XXXXXX (Month+Day + 6 random characters)</p>
                </CardContent>
              </Card>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Custom Statuses Tab */}
        <TabsContent value="statuses">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Palette className="h-5 w-5" />
                    Custom Ticket Statuses
                  </CardTitle>
                  <CardDescription>
                    Add, edit, or remove custom ticket status options
                  </CardDescription>
                </div>
                <Dialog open={statusDialogOpen} onOpenChange={(open) => {
                  setStatusDialogOpen(open);
                  if (!open) resetStatusForm();
                }}>
                  <DialogTrigger asChild>
                    <Button data-testid="add-status-button">
                      <Plus className="h-4 w-4 mr-2" />
                      Add Status
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="co-scope co-settings">
                    <DialogHeader>
                      <DialogTitle>{editingStatus ? 'Edit Status' : 'Create New Status'}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div>
                        <Label>Status Name (Internal)</Label>
                        <Input
                          value={statusForm.name}
                          onChange={(e) => setStatusForm({...statusForm, name: e.target.value})}
                          placeholder="e.g., awaiting_parts"
                          data-testid="status-name-input"
                        />
                        <p className="text-xs text-gray-500 mt-1">Lowercase, use underscores (e.g., waiting_customer)</p>
                      </div>

                      <div>
                        <Label>Display Name</Label>
                        <Input
                          value={statusForm.display_name}
                          onChange={(e) => setStatusForm({...statusForm, display_name: e.target.value})}
                          placeholder="e.g., Awaiting Parts"
                          data-testid="status-display-name-input"
                        />
                        <p className="text-xs text-gray-500 mt-1">How it appears in the UI</p>
                      </div>

                      <div>
                        <Label>Badge Color</Label>
                        <Select
                          value={statusForm.color}
                          onValueChange={(value) => setStatusForm({...statusForm, color: value})}
                        >
                          <SelectTrigger data-testid="status-color-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {colorOptions.map(option => (
                              <SelectItem key={option.value} value={option.value}>
                                <div className="flex items-center gap-2">
                                  <Badge className={option.class}>{option.label}</Badge>
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label>Display Order</Label>
                        <Input
                          type="number"
                          value={statusForm.order}
                          onChange={(e) => setStatusForm({...statusForm, order: parseInt(e.target.value)})}
                          data-testid="status-order-input"
                        />
                        <p className="text-xs text-gray-500 mt-1">Lower numbers appear first</p>
                      </div>

                      <div className="flex items-center gap-2">
                        <Checkbox
                          checked={statusForm.is_active}
                          onCheckedChange={(checked) => setStatusForm({...statusForm, is_active: checked})}
                          data-testid="status-active-checkbox"
                        />
                        <Label>Active</Label>
                      </div>

                      <div className="flex justify-end gap-2">
                        <Button variant="outline" onClick={() => setStatusDialogOpen(false)}>
                          Cancel
                        </Button>
                        <Button onClick={handleSaveStatus} data-testid="save-status-button">
                          <Save className="h-4 w-4 mr-2" />
                          {editingStatus ? 'Update' : 'Create'} Status
                        </Button>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              {statuses.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Ticket className="h-12 w-12 mx-auto text-gray-400 mb-2" />
                  <p>No custom statuses created yet</p>
                  <p className="text-sm mt-1">Add custom statuses to match your workflow</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Order</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Display Name</TableHead>
                      <TableHead>Color</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {statuses.map((status) => {
                      const colorClass = colorOptions.find(c => c.value === status.color)?.class || 'bg-gray-100 text-gray-800';
                      return (
                        <TableRow key={status.id}>
                          <TableCell>{status.order}</TableCell>
                          <TableCell className="font-mono text-sm">{status.name}</TableCell>
                          <TableCell>{status.display_name}</TableCell>
                          <TableCell>
                            <Badge className={colorClass}>{status.color}</Badge>
                          </TableCell>
                          <TableCell>
                            {status.is_active ? (
                              <Badge className="bg-green-100 text-green-800">Active</Badge>
                            ) : (
                              <Badge className="bg-gray-100 text-gray-800">Inactive</Badge>
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-2">
                              <Button size="sm" variant="outline" onClick={() => handleEditStatus(status)}>
                                <Edit className="h-3 w-3" />
                              </Button>
                              <Button size="sm" variant="ghost" onClick={() => handleDeleteStatus(status.id)}>
                                <Trash2 className="h-3 w-3 text-red-600" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Ticket Boards Tab */}
        <TabsContent value="boards">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Layout className="h-5 w-5" />
                    Ticket Boards
                  </CardTitle>
                  <CardDescription>
                    Create different boards for organizing tickets (e.g., Support, Projects, Maintenance)
                  </CardDescription>
                </div>
                <Dialog open={boardDialogOpen} onOpenChange={(open) => {
                  setBoardDialogOpen(open);
                  if (!open) resetBoardForm();
                }}>
                  <DialogTrigger asChild>
                    <Button data-testid="add-board-button">
                      <Plus className="h-4 w-4 mr-2" />
                      Add Board
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="co-scope co-settings">
                    <DialogHeader>
                      <DialogTitle>{editingBoard ? 'Edit Board' : 'Create New Board'}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div>
                        <Label>Board Name</Label>
                        <Input
                          value={boardForm.name}
                          onChange={(e) => setBoardForm({...boardForm, name: e.target.value})}
                          placeholder="e.g., Support Tickets"
                          data-testid="board-name-input"
                        />
                      </div>

                      <div>
                        <Label>Description</Label>
                        <Textarea
                          value={boardForm.description}
                          onChange={(e) => setBoardForm({...boardForm, description: e.target.value})}
                          placeholder="Describe the purpose of this board..."
                          rows={3}
                          data-testid="board-description-input"
                        />
                      </div>

                      <div>
                        <Label>Board Type</Label>
                        <Select
                          value={boardForm.board_type}
                          onValueChange={(value) => setBoardForm({...boardForm, board_type: value})}
                        >
                          <SelectTrigger data-testid="board-type-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="kanban">Kanban</SelectItem>
                            <SelectItem value="list">List View</SelectItem>
                            <SelectItem value="timeline">Timeline</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="flex items-center gap-2">
                        <Checkbox
                          checked={boardForm.is_active}
                          onCheckedChange={(checked) => setBoardForm({...boardForm, is_active: checked})}
                          data-testid="board-active-checkbox"
                        />
                        <Label>Active</Label>
                      </div>

                      <div className="flex items-center gap-2">
                        <Checkbox
                          checked={boardForm.is_default || false}
                          onCheckedChange={(checked) => setBoardForm({...boardForm, is_default: checked})}
                          data-testid="board-default-checkbox"
                        />
                        <Label>Set as Default Board</Label>
                      </div>
                      <p className="text-xs text-gray-500 -mt-2">
                        Default board is used for emails that don't match any routing rules
                      </p>

                      <div className="flex justify-end gap-2">
                        <Button variant="outline" onClick={() => setBoardDialogOpen(false)}>
                          Cancel
                        </Button>
                        <Button onClick={handleSaveBoard} data-testid="save-board-button">
                          <Save className="h-4 w-4 mr-2" />
                          {editingBoard ? 'Update' : 'Create'} Board
                        </Button>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              {boards.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Layout className="h-12 w-12 mx-auto text-gray-400 mb-2" />
                  <p>No boards created yet</p>
                  <p className="text-sm mt-1">Create boards to organize tickets by workflow or team</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {boards.map((board) => (
                    <Card key={board.id} className={`hover:shadow-md transition-shadow ${board.is_default ? 'border-2 border-blue-500' : ''}`}>
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <h3 className="font-semibold text-lg">{board.name}</h3>
                              {board.is_default && (
                                <Badge className="bg-blue-100 text-blue-800">
                                  <SettingsIcon className="h-3 w-3 mr-1 inline" />
                                  Default
                                </Badge>
                              )}
                            </div>
                            {board.description && (
                              <p className="text-sm text-gray-600 mt-1">{board.description}</p>
                            )}
                          </div>
                          {board.is_active ? (
                            <Badge className="bg-green-100 text-green-800">Active</Badge>
                          ) : (
                            <Badge className="bg-gray-100 text-gray-800">Inactive</Badge>
                          )}
                        </div>
                        <div className="text-xs text-gray-500 mb-3">
                          Type: <span className="capitalize font-semibold">{board.board_type}</span>
                        </div>
                        <div className="flex gap-2">
                          {!board.is_default && board.is_active && (
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={async () => {
                                try {
                                  await api.put(`/ticket-boards/${board.id}`, { is_default: true });
                                  toast.success(`${board.name} set as default board`);
                                  loadData();
                                } catch (error) {
                                  toast.error('Failed to set default board');
                                }
                              }}
                              className="text-blue-600"
                            >
                              <SettingsIcon className="h-3 w-3 mr-1" />
                              Set as Default
                            </Button>
                          )}
                          <Button size="sm" variant="outline" onClick={() => handleEditBoard(board)}>
                            <Edit className="h-3 w-3 mr-1" />
                            Edit
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => handleDeleteBoard(board.id)}>
                            <Trash2 className="h-3 w-3 text-red-600" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>


        {/* Notifications Tab */}
        <TabsContent value="notifications">
          <NotificationSettings />
        </TabsContent>


        {/* Template Responses Tab */}
        <TabsContent value="templates">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <MessageSquare className="h-5 w-5" />
                    Template Responses (Quick Replies)
                  </CardTitle>
                  <CardDescription>
                    Create reusable response templates with variables and trigger keywords
                  </CardDescription>
                </div>
                <Dialog open={templateDialogOpen} onOpenChange={(open) => {
                  setTemplateDialogOpen(open);
                  if (!open) resetTemplateForm();
                }}>
                  <DialogTrigger asChild>
                    <Button data-testid="add-template-button">
                      <Plus className="h-4 w-4 mr-2" />
                      Create Template
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="co-scope co-settings max-w-3xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>{editingTemplate ? 'Edit Template' : 'Create Template Response'}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Template Name *</Label>
                          <Input
                            value={templateForm.name}
                            onChange={(e) => setTemplateForm({...templateForm, name: e.target.value})}
                            placeholder="e.g., Hardware Issue Response"
                            data-testid="template-name-input"
                          />
                        </div>
                        <div>
                          <Label>Category</Label>
                          <Select
                            value={templateForm.category || undefined}
                            onValueChange={(value) => setTemplateForm({...templateForm, category: value})}
                          >
                            <SelectTrigger data-testid="template-category-select">
                              <SelectValue placeholder="Select category" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="hardware">Hardware</SelectItem>
                              <SelectItem value="software">Software</SelectItem>
                              <SelectItem value="network">Network</SelectItem>
                              <SelectItem value="security">Security</SelectItem>
                              <SelectItem value="general">General</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <div>
                        <Label>Trigger Keywords</Label>
                        <div className="flex gap-2 mb-2">
                          <Input
                            value={keywordInput}
                            onChange={(e) => setKeywordInput(e.target.value)}
                            placeholder="e.g., printer, hardware"
                            onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addKeyword())}
                            data-testid="keyword-input"
                          />
                          <Button type="button" onClick={addKeyword} size="sm">
                            <Plus className="h-4 w-4" />
                          </Button>
                        </div>
                        <div className="flex gap-2 flex-wrap">
                          {templateForm.trigger_keywords.map((keyword) => (
                            <Badge key={keyword} variant="secondary" className="gap-1">
                              {keyword}
                              <XIcon className="h-3 w-3 cursor-pointer" onClick={() => removeKeyword(keyword)} />
                            </Badge>
                          ))}
                        </div>
                        <p className="text-xs text-gray-500 mt-1">Keywords to auto-suggest this template</p>
                      </div>

                      <div>
                        <Label>Available Variables</Label>
                        <div className="flex gap-2 mb-2">
                          <Input
                            value={variableInput}
                            onChange={(e) => setVariableInput(e.target.value)}
                            placeholder="e.g., customer_name, ticket_number"
                            onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addVariable())}
                            data-testid="variable-input"
                          />
                          <Button type="button" onClick={addVariable} size="sm">
                            <Plus className="h-4 w-4" />
                          </Button>
                        </div>
                        <div className="flex gap-2 flex-wrap">
                          {templateForm.variables.map((variable) => (
                            <Badge key={variable} className="bg-blue-100 text-blue-800 gap-1 cursor-pointer" onClick={() => insertVariable(variable)}>
                              {`{{${variable}}}`}
                              <XIcon className="h-3 w-3" onClick={(e) => { e.stopPropagation(); removeVariable(variable); }} />
                            </Badge>
                          ))}
                        </div>
                        <p className="text-xs text-gray-500 mt-1">Click a variable to insert into template text</p>
                      </div>

                      <div>
                        <Label>Template Text *</Label>
                        <Textarea
                          value={templateForm.template_text}
                          onChange={(e) => setTemplateForm({...templateForm, template_text: e.target.value})}
                          placeholder="Dear {{customer_name}},\n\nThank you for contacting us regarding ticket {{ticket_number}}...\n\nBest regards,\n{{technician_name}}"
                          rows={8}
                          data-testid="template-text-input"
                          className="font-mono text-sm"
                        />
                        <p className="text-xs text-gray-500 mt-1">Use {'{{variable_name}}'} for dynamic content</p>
                      </div>

                      <div className="flex items-center gap-2">
                        <Checkbox
                          checked={templateForm.is_active}
                          onCheckedChange={(checked) => setTemplateForm({...templateForm, is_active: checked})}
                        />
                        <Label>Active</Label>
                      </div>

                      <div className="flex justify-end gap-2">
                        <Button variant="outline" onClick={() => setTemplateDialogOpen(false)}>
                          Cancel
                        </Button>
                        <Button onClick={handleSaveTemplate} data-testid="save-template-button">
                          <Save className="h-4 w-4 mr-2" />
                          {editingTemplate ? 'Update' : 'Create'} Template
                        </Button>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              {templateResponses.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <MessageSquare className="h-12 w-12 mx-auto text-gray-400 mb-2" />
                  <p>No template responses created yet</p>
                  <p className="text-sm mt-1">Create reusable responses to speed up ticket replies</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-4">
                  {templateResponses.map((template) => (
                    <Card key={template.id} className="hover:shadow-md transition-shadow">
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <h3 className="font-semibold text-lg">{template.name}</h3>
                              {template.category && (
                                <Badge variant="outline" className="text-xs">{template.category}</Badge>
                              )}
                              {template.is_active ? (
                                <Badge className="bg-green-100 text-green-800 text-xs">Active</Badge>
                              ) : (
                                <Badge className="bg-gray-100 text-gray-800 text-xs">Inactive</Badge>
                              )}
                            </div>
                            
                            {template.trigger_keywords && template.trigger_keywords.length > 0 && (
                              <div className="flex gap-1 mb-2 flex-wrap">
                                <span className="text-xs text-gray-500">Triggers:</span>
                                {template.trigger_keywords.map((kw) => (
                                  <Badge key={kw} variant="secondary" className="text-xs">{kw}</Badge>
                                ))}
                              </div>
                            )}

                            <div className="bg-gray-50 p-3 rounded border border-gray-200 mt-2">
                              <p className="text-sm font-mono whitespace-pre-wrap">{template.template_text}</p>
                            </div>

                            {template.variables && template.variables.length > 0 && (
                              <div className="flex gap-1 mt-2 flex-wrap">
                                <span className="text-xs text-gray-500">Variables:</span>
                                {template.variables.map((v) => (
                                  <Badge key={v} className="bg-blue-100 text-blue-800 text-xs">{`{{${v}}}`}</Badge>
                                ))}
                              </div>
                            )}
                          </div>
                          
                          <div className="flex gap-2 ml-4">
                            <Button size="sm" variant="outline" onClick={() => handleEditTemplate(template)}>
                              <Edit className="h-3 w-3" />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={async () => {
                              const success = await copyToClipboard(template.template_text);
                              if (success) {
                                toast.success('Template copied to clipboard');
                              } else {
                                toast.error('Copy failed');
                              }
                            }}>
                              <Copy className="h-3 w-3" />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => handleDeleteTemplate(template.id)}>
                              <Trash2 className="h-3 w-3 text-red-600" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Email Templates Tab */}
        <TabsContent value="emails">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Mail className="h-5 w-5" />
                    Email Templates
                  </CardTitle>
                  <CardDescription>
                    Create rich HTML email templates for ticket notifications
                  </CardDescription>
                </div>
                <Dialog open={emailTemplateDialogOpen} onOpenChange={(open) => {
                  setEmailTemplateDialogOpen(open);
                  if (!open) resetEmailTemplateForm();
                }}>
                  <DialogTrigger asChild>
                    <Button data-testid="add-email-template-button">
                      <Plus className="h-4 w-4 mr-2" />
                      Create Email Template
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="co-scope co-settings max-w-5xl max-h-[95vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>{editingEmailTemplate ? 'Edit Email Template' : 'Create Email Template'}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Template Name *</Label>
                          <Input
                            value={emailTemplateForm.name}
                            onChange={(e) => setEmailTemplateForm({...emailTemplateForm, name: e.target.value})}
                            placeholder="e.g., Ticket Created Notification"
                            data-testid="email-template-name-input"
                          />
                        </div>
                        <div>
                          <Label>Template Type *</Label>
                          <Select
                            value={emailTemplateForm.template_type}
                            onValueChange={(value) => setEmailTemplateForm({...emailTemplateForm, template_type: value})}
                          >
                            <SelectTrigger data-testid="email-template-type-select">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="ticket_created">Ticket Created</SelectItem>
                              <SelectItem value="ticket_assigned">Ticket Assigned</SelectItem>
                              <SelectItem value="status_changed">Status Changed</SelectItem>
                              <SelectItem value="ticket_resolved">Ticket Resolved</SelectItem>
                              <SelectItem value="ticket_closed">Ticket Closed</SelectItem>
                              <SelectItem value="custom">Custom Template</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <div>
                        <Label>Description (optional)</Label>
                        <Input
                          value={emailTemplateForm.description}
                          onChange={(e) => setEmailTemplateForm({...emailTemplateForm, description: e.target.value})}
                          placeholder="Brief description of when to use this template"
                          data-testid="email-template-description-input"
                        />
                      </div>

                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <Label>Email Subject *</Label>
                          <div className="flex gap-1">
                            <span className="text-xs text-gray-500">Insert merge field:</span>
                            <Select onValueChange={(value) => insertMergeField(value)}>
                              <SelectTrigger className="h-7 w-[180px] text-xs">
                                <SelectValue placeholder="Select field" />
                              </SelectTrigger>
                              <SelectContent>
                                {availableMergeFields.map((field) => (
                                  <SelectItem key={field} value={field} className="text-xs">
                                    {`{{${field}}}`}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <Input
                          value={emailTemplateForm.subject}
                          onChange={(e) => setEmailTemplateForm({...emailTemplateForm, subject: e.target.value})}
                          placeholder="Ticket {{ticket_number}} - {{ticket_title}}"
                          data-testid="email-subject-input"
                        />
                      </div>

                      <div>
                        <Label>Email Body (HTML) *</Label>
                        <RichTextEditor
                          value={emailTemplateForm.body_html}
                          onChange={(value) => setEmailTemplateForm({...emailTemplateForm, body_html: value})}
                          placeholder="Start composing your email template..."
                        />
                        <p className="text-xs text-gray-500 mt-1">Rich text editor - use merge fields like {'{{customer_name}}'}</p>
                      </div>

                      <div>
                        <Label>Available Merge Fields</Label>
                        <div className="flex gap-2 flex-wrap p-3 bg-gray-50 rounded border border-gray-200">
                          {availableMergeFields.map((field) => (
                            <Badge
                              key={field}
                              className="bg-blue-100 text-blue-800 cursor-pointer hover:bg-blue-200"
                              onClick={async () => {
                                const placeholder = `{{${field}}}`;
                                const success = await copyToClipboard(placeholder);
                                if (success) {
                                  toast.success(`${placeholder} copied to clipboard`);
                                } else {
                                  toast.error('Copy failed');
                                }
                              }}
                            >
                              {`{{${field}}}`}
                            </Badge>
                          ))}
                        </div>
                        <p className="text-xs text-gray-500 mt-1">Click to copy merge field to clipboard</p>
                      </div>

                      <div className="flex items-center gap-2">
                        <Checkbox
                          checked={emailTemplateForm.is_active}
                          onCheckedChange={(checked) => setEmailTemplateForm({...emailTemplateForm, is_active: checked})}
                        />
                        <Label>Active</Label>
                      </div>

                      <div className="flex justify-end gap-2">
                        <Button variant="outline" onClick={() => setEmailTemplateDialogOpen(false)}>
                          Cancel
                        </Button>
                        <Button variant="outline" onClick={handlePreviewEmail} data-testid="preview-email-button">
                          <Eye className="h-4 w-4 mr-2" />
                          Preview
                        </Button>
                        <Button onClick={handleSaveEmailTemplate} data-testid="save-email-template-button">
                          <Save className="h-4 w-4 mr-2" />
                          {editingEmailTemplate ? 'Update' : 'Create'} Template
                        </Button>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              {emailTemplates.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Mail className="h-12 w-12 mx-auto text-gray-400 mb-2" />
                  <p>No email templates created yet</p>
                  <p className="text-sm mt-1">Create email templates for automated notifications</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-4">
                  {emailTemplates.map((template) => (
                    <Card key={template.id} className="hover:shadow-md transition-shadow">
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <h3 className="font-semibold text-lg">{template.name}</h3>
                              <Badge variant="outline" className="text-xs capitalize">
                                {template.template_type.replace('_', ' ')}
                              </Badge>
                              {template.is_active ? (
                                <Badge className="bg-green-100 text-green-800 text-xs">Active</Badge>
                              ) : (
                                <Badge className="bg-gray-100 text-gray-800 text-xs">Inactive</Badge>
                              )}
                            </div>
                            
                            {template.description && (
                              <p className="text-sm text-gray-600 mb-2">{template.description}</p>
                            )}

                            <div className="space-y-2">
                              <div>
                                <span className="text-xs font-semibold text-gray-700">Subject:</span>
                                <p className="text-sm font-mono bg-gray-50 p-2 rounded border border-gray-200">
                                  {template.subject}
                                </p>
                              </div>
                              
                              <div>
                                <span className="text-xs font-semibold text-gray-700">Preview:</span>
                                <div 
                                  className="text-sm bg-white p-3 rounded border border-gray-200 max-h-32 overflow-y-auto"
                                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(template.body_html) }}
                                />
                              </div>
                            </div>
                          </div>
                          
                          <div className="flex gap-2 ml-4">
                            <Button size="sm" variant="outline" onClick={() => handleEditEmailTemplate(template)}>
                              <Edit className="h-3 w-3" />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => handleDeleteEmailTemplate(template.id)}>
                              <Trash2 className="h-3 w-3 text-red-600" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Auto-Reply Tab */}
        <TabsContent value="autoreply">
          <AutoReplySettings />
        </TabsContent>

        {/* Asset Routing Rules Tab */}
        <TabsContent value="routing">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2"><Zap className="h-5 w-5" /> Asset Routing Rules</CardTitle>
                  <CardDescription>Auto-suggest board and priority when assets are linked to tickets. Rules are matched in order — first match wins.</CardDescription>
                </div>
                <Dialog open={routingDialogOpen} onOpenChange={(open) => { setRoutingDialogOpen(open); if (!open) { setEditingRule(null); setRoutingForm({ name: '', asset_type: '', asset_status: '', target_board_id: '', target_board_name: '', suggested_priority: 'medium', is_active: true, priority_order: 99, auto_assign: false, assign_strategy: 'round_robin', assign_technician_id: '', assign_technician_name: '' }); } }}>
                  <DialogTrigger asChild>
                    <Button data-testid="add-routing-rule-btn"><Plus className="h-4 w-4 mr-2" />Add Rule</Button>
                  </DialogTrigger>
                  <DialogContent className="co-scope co-settings">
                    <DialogHeader>
                      <DialogTitle>{editingRule ? 'Edit' : 'New'} Routing Rule</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div>
                        <Label>Rule Name *</Label>
                        <Input value={routingForm.name} onChange={(e) => setRoutingForm({...routingForm, name: e.target.value})} placeholder="e.g. Server Maintenance → Critical" data-testid="routing-name-input" />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Asset Type</Label>
                          <Select value={routingForm.asset_type || '__any'} onValueChange={(v) => setRoutingForm({...routingForm, asset_type: v === '__any' ? '' : v})}>
                            <SelectTrigger data-testid="routing-asset-type"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__any">Any Type</SelectItem>
                              <SelectItem value="device">Device</SelectItem>
                              <SelectItem value="workstation">Workstation</SelectItem>
                              <SelectItem value="server">Server</SelectItem>
                              <SelectItem value="network">Network</SelectItem>
                              <SelectItem value="printer">Printer</SelectItem>
                              <SelectItem value="mobile">Mobile</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label>Asset Status</Label>
                          <Select value={routingForm.asset_status || '__any'} onValueChange={(v) => setRoutingForm({...routingForm, asset_status: v === '__any' ? '' : v})}>
                            <SelectTrigger data-testid="routing-asset-status"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__any">Any Status</SelectItem>
                              <SelectItem value="active">Active</SelectItem>
                              <SelectItem value="inactive">Inactive</SelectItem>
                              <SelectItem value="maintenance">Maintenance</SelectItem>
                              <SelectItem value="retired">Retired</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Suggest Board</Label>
                          <Select value={routingForm.target_board_id || '__none'} onValueChange={(v) => setRoutingForm({...routingForm, target_board_id: v === '__none' ? '' : v})}>
                            <SelectTrigger data-testid="routing-board"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__none">No board suggestion</SelectItem>
                              {boards.filter(b => b.is_active).map(b => (
                                <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label>Suggest Priority</Label>
                          <Select value={routingForm.suggested_priority} onValueChange={(v) => setRoutingForm({...routingForm, suggested_priority: v})}>
                            <SelectTrigger data-testid="routing-priority"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="low">Low</SelectItem>
                              <SelectItem value="medium">Medium</SelectItem>
                              <SelectItem value="high">High</SelectItem>
                              <SelectItem value="critical">Critical</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Checkbox checked={routingForm.is_active} onCheckedChange={(c) => setRoutingForm({...routingForm, is_active: !!c})} data-testid="routing-active-check" />
                        <Label>Active</Label>
                      </div>
                      <div className="border-t pt-4 space-y-3">
                        <div className="flex items-center gap-2">
                          <Checkbox checked={routingForm.auto_assign} onCheckedChange={(c) => setRoutingForm({...routingForm, auto_assign: !!c})} data-testid="routing-auto-assign-check" />
                          <Label className="flex items-center gap-1.5"><UserCog className="h-4 w-4" /> Auto-assign technician</Label>
                        </div>
                        {routingForm.auto_assign && (
                          <div className="ml-6 space-y-3">
                            <div>
                              <Label>Assignment Strategy</Label>
                              <Select value={routingForm.assign_strategy} onValueChange={(v) => setRoutingForm({...routingForm, assign_strategy: v})}>
                                <SelectTrigger data-testid="routing-assign-strategy"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="round_robin">Round Robin</SelectItem>
                                  <SelectItem value="least_loaded">Least Loaded</SelectItem>
                                  <SelectItem value="specific">Specific Technician</SelectItem>
                                </SelectContent>
                              </Select>
                              <p className="text-xs text-gray-500 mt-1">
                                {routingForm.assign_strategy === 'round_robin' && 'Rotate assignments among all technicians'}
                                {routingForm.assign_strategy === 'least_loaded' && 'Assign to technician with fewest open tickets'}
                                {routingForm.assign_strategy === 'specific' && 'Always assign to a chosen technician'}
                              </p>
                            </div>
                            {routingForm.assign_strategy === 'specific' && (
                              <div>
                                <Label>Technician</Label>
                                <Select value={routingForm.assign_technician_id || '__none'} onValueChange={(v) => setRoutingForm({...routingForm, assign_technician_id: v === '__none' ? '' : v})}>
                                  <SelectTrigger data-testid="routing-technician-select"><SelectValue placeholder="Select technician" /></SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="__none">Select technician</SelectItem>
                                    {technicians.map(t => (
                                      <SelectItem key={t.email} value={t.email}>{t.display_name || t.email}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="flex justify-end gap-2">
                        <Button variant="outline" onClick={() => setRoutingDialogOpen(false)}>Cancel</Button>
                        <Button onClick={handleSaveRoutingRule} disabled={!routingForm.name} data-testid="save-routing-rule-btn"><Save className="h-4 w-4 mr-2" />Save Rule</Button>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              {routingRules.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No routing rules configured. Add a rule to auto-suggest board and priority based on linked assets.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-8">#</TableHead>
                      <TableHead>Rule Name</TableHead>
                      <TableHead>Asset Type</TableHead>
                      <TableHead>Asset Status</TableHead>
                      <TableHead>Board</TableHead>
                      <TableHead>Priority</TableHead>
                      <TableHead>Auto-Assign</TableHead>
                      <TableHead>Active</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {routingRules.map((rule, idx) => (
                      <TableRow key={rule.id} data-testid={`routing-rule-${rule.id}`}>
                        <TableCell className="text-gray-400 text-sm">{idx + 1}</TableCell>
                        <TableCell className="font-medium">{rule.name}</TableCell>
                        <TableCell>{rule.asset_type ? <Badge variant="outline" className="capitalize">{rule.asset_type}</Badge> : <span className="text-gray-400">Any</span>}</TableCell>
                        <TableCell>{rule.asset_status ? <Badge variant="outline" className="capitalize">{rule.asset_status}</Badge> : <span className="text-gray-400">Any</span>}</TableCell>
                        <TableCell>{rule.target_board_name || <span className="text-gray-400">—</span>}</TableCell>
                        <TableCell><Badge className={`capitalize ${({critical:'bg-red-100 text-red-800',high:'bg-orange-100 text-orange-800',medium:'bg-yellow-100 text-yellow-800',low:'bg-blue-100 text-blue-800'})[rule.suggested_priority] || ''}`}>{rule.suggested_priority}</Badge></TableCell>
                        <TableCell>
                          {rule.auto_assign ? (
                            <span className="inline-flex items-center gap-1 text-xs">
                              <UserCog className="h-3 w-3 text-blue-600" />
                              <span className="capitalize">{(rule.assign_strategy || 'round_robin').replace('_', ' ')}</span>
                              {rule.assign_strategy === 'specific' && rule.assign_technician_name && <span className="text-gray-500">({rule.assign_technician_name})</span>}
                            </span>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </TableCell>
                        <TableCell>{rule.is_active ? <Badge className="bg-green-100 text-green-800">Active</Badge> : <Badge variant="outline">Inactive</Badge>}</TableCell>
                        <TableCell className="text-right">
                          <Button size="sm" variant="ghost" onClick={() => openEditRule(rule)} data-testid={`edit-rule-${rule.id}`}><Edit className="h-3 w-3" /></Button>
                          <Button size="sm" variant="ghost" onClick={() => handleDeleteRoutingRule(rule.id)} data-testid={`delete-rule-${rule.id}`}><Trash2 className="h-3 w-3 text-red-600" /></Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Email Preview Dialog */}
      <Dialog open={previewDialogOpen} onOpenChange={setPreviewDialogOpen}>
        <DialogContent className="co-scope co-settings max-w-3xl">
          <DialogHeader>
            <DialogTitle>Email Preview</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-sm font-semibold">Subject:</Label>
              <p className="text-sm bg-gray-50 p-3 rounded border border-gray-200 mt-1">
                {previewContent.subject || '(Empty subject)'}
              </p>
            </div>
            <div>
              <Label className="text-sm font-semibold">Body:</Label>
              <div 
                className="bg-white p-4 rounded border border-gray-200 mt-1 min-h-[200px]"
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(previewContent.body || '<p>(Empty body)</p>') }}
              />
            </div>
            <p className="text-xs text-gray-500">This is a preview with sample data. Actual emails will use real ticket data.</p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
